import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MapPin } from "lucide-react";
import PlacesMap from "@/components/PlacesMap";
import CoverageChip from "@/components/CoverageChip";
import { useSeo } from "@/hooks/useSeo";

/**
 * /places — Slice M · Universal live POI map.
 *
 * Location resolution:
 *   1. localStorage.f2n_last_center (from another app screen)
 *   2. navigator.geolocation (async, one-shot)
 *   3. Mumbai fallback so the map always renders
 */
export default function PlacesLive() {
  const [center, setCenter] = useState(() => {
    try {
      const raw = localStorage.getItem("f2n_last_center");
      if (raw) {
        const p = JSON.parse(raw);
        if (Array.isArray(p) && p.length === 2 && Number.isFinite(p[0]) && Number.isFinite(p[1])) return p;
      }
    } catch { /* ignore */ }
    return [19.076, 72.877];
  });

  useSeo({
    title: "Live places map — Find2Near",
    description: "Every point-of-interest Find2Near knows about, deduped worldwide.",
  });

  useEffect(() => {
    // If we already used a persisted centre, don't override — respect the
    // user's last explicit choice. Only ask GPS when we're falling back.
    let cached = null;
    try { cached = localStorage.getItem("f2n_last_center"); } catch { /* ignore */ }
    if (cached) return;

    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const next = [pos.coords.latitude, pos.coords.longitude];
        setCenter(next);
        try { localStorage.setItem("f2n_last_center", JSON.stringify(next)); } catch { /* ignore */ }
      },
      () => { /* denied — keep Mumbai fallback */ },
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 5 * 60 * 1000 },
    );
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="places-live-page">
      <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-stone-100">
        <Link to="/" className="p-2 -ml-2 rounded-full hover:bg-stone-100" data-testid="places-back">
          <ArrowLeft size={20} />
        </Link>
        <MapPin size={18} className="text-emerald-700" />
        <h1 className="heading font-black text-xl text-stone-900">Live places map</h1>
        <div className="ml-auto"><CoverageChip /></div>
      </div>
      <div className="flex-1">
        <PlacesMap key={`${center[0]}-${center[1]}`} center={center} zoom={15} height="calc(100vh - 60px)" />
      </div>
    </div>
  );
}
